import React, { useState, useRef, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { ArrowLeft, ArrowRight, Sparkles, Check } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Slider } from '@/components/ui/Slider';
import { cn } from 'src/utils/Functions';
import {
  FORM_FIELDS,
  INITIAL_FORM_DATA,
  CONVERSATION_SCRIPT,
  type RecordingAgreementFormData,
  type FormFieldDef,
} from './chat-script';
import { RecordingAgreementPreview } from './RecordingAgreementPreview';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RecordingAgreementFlowProps {
  onComplete?: (data: {
    template: { id: string; name: string };
    formData: RecordingAgreementFormData;
    method: 'template';
  }) => void;
  onBack?: () => void;
}

interface ChatMessage {
  id: string;
  role: 'assistant' | 'user';
  text: string;
}

// ---------------------------------------------------------------------------
// Typing Indicator
// ---------------------------------------------------------------------------

const TypingIndicator: React.FC<{ side: 'left' | 'right' }> = ({ side }) => (
  <div className={cn('flex', side === 'right' ? 'justify-end' : 'justify-start')}>
    <div
      className={cn(
        'rounded-2xl px-4 py-3',
        side === 'right'
          ? 'bg-primary/20 rounded-br-md'
          : 'bg-white/5 rounded-bl-md'
      )}
    >
      <div className="flex items-center gap-1">
        {[0, 1, 2].map((i) => (
          <motion.span
            key={i}
            className={cn(
              'size-1.5 rounded-full',
              side === 'right' ? 'bg-primary/60' : 'bg-white/40'
            )}
            animate={{ opacity: [0.3, 1, 0.3] }}
            transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
          />
        ))}
      </div>
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Chat Message Bubble
// ---------------------------------------------------------------------------

const MessageBubble: React.FC<{ message: ChatMessage }> = ({ message }) => {
  const isUser = message.role === 'user';

  const renderText = (text: string) => {
    if (!text) return null;
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
      if (part.startsWith('**') && part.endsWith('**')) {
        return (
          <span key={i} className="font-semibold text-white">
            {part.slice(2, -2)}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={cn('flex', isUser ? 'justify-end' : 'justify-start')}
    >
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-md'
            : 'bg-white/5 text-white/80 rounded-bl-md'
        )}
      >
        {isUser ? message.text : renderText(message.text)}
      </div>
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Form field (read-only for demo)
// ---------------------------------------------------------------------------

const FormField: React.FC<{
  field: FormFieldDef;
  value: string | number;
  justFilled?: boolean;
}> = ({ field, value, justFilled }) => {
  const hasValue = value !== '' && value !== 0;

  return (
    <motion.div
      layout
      className={cn(
        'rounded-xl border p-4 transition-all duration-700',
        justFilled
          ? 'bg-primary/5 border-primary/30'
          : hasValue
            ? 'bg-white/[0.03] border-white/10'
            : 'bg-white/[0.02] border-white/5'
      )}
    >
      <div className="flex items-center justify-between mb-1.5">
        <label className="text-white text-sm font-medium">{field.label}</label>
        {hasValue && (
          <motion.div
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: 'spring', stiffness: 500, damping: 25 }}
          >
            <Check className="size-3.5 text-green-400" />
          </motion.div>
        )}
      </div>
      <p className="text-white/30 text-xs mb-2.5">{field.description}</p>

      {field.inputType === 'text' && (
        <div
          className={cn(
            'w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm min-h-[36px]',
            hasValue ? 'text-white' : 'text-white/20'
          )}
        >
          {(value as string) || field.placeholder}
        </div>
      )}

      {field.inputType === 'currency' && (
        <div
          className={cn(
            'w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm min-h-[36px]',
            hasValue ? 'text-white' : 'text-white/20'
          )}
        >
          {hasValue ? `$${value}` : `$${field.placeholder}`}
        </div>
      )}

      {field.inputType === 'slider' && (
        <div className="space-y-2 pt-1">
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/40">Artist</span>
            <span className="text-white font-medium">
              {(value as number) || 0}% / {100 - ((value as number) || 0)}%
            </span>
            <span className="text-white/40">Label</span>
          </div>
          <Slider
            value={(value as number) || 0}
            min={0}
            max={100}
            step={5}
            disabled
          />
        </div>
      )}

      {field.inputType === 'select' && (
        <div className="flex flex-wrap gap-1.5">
          {field.options?.map((option) => (
            <span
              key={option}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium border',
                value === option
                  ? 'bg-primary text-primary-foreground border-primary'
                  : 'bg-white/5 text-white/30 border-white/5'
              )}
            >
              {option}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
};

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export const RecordingAgreementFlow: React.FC<RecordingAgreementFlowProps> = ({
  onComplete,
  onBack,
}) => {
  const [formData, setFormData] = useState<RecordingAgreementFormData>(INITIAL_FORM_DATA);
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [typingSide, setTypingSide] = useState<'left' | 'right'>('left');
  const [recentlyFilled, setRecentlyFilled] = useState<Set<string>>(new Set());
  const [showPreview, setShowPreview] = useState(false);
  const [scriptDone, setScriptDone] = useState(false);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const generationRef = useRef(0);

  // Scroll chat to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, isTyping, scrollToBottom]);

  // --- Auto-play the entire scripted conversation ---
  useEffect(() => {
    // Each effect run gets a unique generation; stale timers from previous runs bail out
    const gen = ++generationRef.current;
    const alive = () => gen === generationRef.current;

    // Reset state
    setChatMessages([]);
    setFormData(INITIAL_FORM_DATA);
    setShowPreview(false);
    setScriptDone(false);
    setRecentlyFilled(new Set());

    let lineIdx = 0;

    const playNextLine = () => {
      if (!alive()) return;
      if (lineIdx >= CONVERSATION_SCRIPT.length) {
        setTimeout(() => {
          if (!alive()) return;
          setShowPreview(true);
          setScriptDone(true);
        }, 1200);
        return;
      }

      const line = CONVERSATION_SCRIPT[lineIdx];
      const currentIdx = lineIdx;

      const side = line.role === 'user' ? 'right' : 'left';
      setTypingSide(side);
      setIsTyping(true);

      const baseDelay = line.role === 'user' ? 600 : 800;
      const lengthFactor = Math.min(line.text.length / 80, 1) * 500;
      const typingDuration = baseDelay + lengthFactor;

      setTimeout(() => {
        if (!alive()) return;
        setIsTyping(false);

        setChatMessages((prev) => [
          ...prev,
          {
            id: `msg-${gen}-${currentIdx}`,
            role: line.role,
            text: line.text,
          },
        ]);

        if (line.fills) {
          const fillKeys = Object.keys(line.fills) as (keyof RecordingAgreementFormData)[];
          setTimeout(() => {
            if (!alive()) return;
            setFormData((prev) => ({ ...prev, ...line.fills }));
            setRecentlyFilled(new Set(fillKeys));
            setTimeout(() => {
              if (!alive()) return;
              setRecentlyFilled(new Set());
            }, 2500);
          }, 400);
        }

        lineIdx++;
        const gap = line.role === 'user' ? 1200 : 1000;
        setTimeout(playNextLine, gap);
      }, typingDuration);
    };

    setTimeout(playNextLine, 600);

    return () => {
      // Incrementing generation in the next effect run is what invalidates old timers.
      // No explicit cleanup needed — stale timers check alive() and bail out.
    };
  }, []);

  // Progress
  const filledCount = FORM_FIELDS.filter((f) => {
    const v = formData[f.key];
    return v !== '' && v !== 0;
  }).length;
  const progress = (filledCount / FORM_FIELDS.length) * 100;

  // Create deal
  const handleCreateDeal = () => {
    onComplete?.({
      template: { id: 'recording-deal', name: 'Recording Agreement' },
      formData,
      method: 'template',
    });
  };

  return (
    <div className="flex gap-6 h-[calc(100vh-120px)] max-h-[800px]">
      {/* ================================================================ */}
      {/* Left Pane — Chat (auto-playing) */}
      {/* ================================================================ */}
      <div className="w-1/2 flex flex-col bg-white/[0.02] rounded-2xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-white/5 flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="p-1 -ml-1 text-white/40 hover:text-white/70 transition-colors"
            >
              <ArrowLeft className="size-4" />
            </button>
          )}
          <div className="flex items-center gap-2">
            <div className="size-7 rounded-full bg-primary/20 flex items-center justify-center">
              <Sparkles className="size-3.5 text-primary" />
            </div>
            <span className="text-white/70 text-sm font-medium">
              Deal Assistant
            </span>
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
          {chatMessages.map((msg) => (
            <MessageBubble key={msg.id} message={msg} />
          ))}
          {isTyping && <TypingIndicator side={typingSide} />}
          <div ref={messagesEndRef} />
        </div>

        {/* Locked input */}
        <div className="px-4 py-3 border-t border-white/5">
          <div
            className="w-full px-4 py-2.5 bg-white/[0.03] border border-white/5 rounded-xl text-white/20 text-sm cursor-not-allowed select-none"
          >
            Watching the conversation...
          </div>
        </div>
      </div>

      {/* ================================================================ */}
      {/* Right Pane — Form → Preview */}
      {/* ================================================================ */}
      <div className="w-1/2 flex flex-col bg-white/[0.02] rounded-2xl border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
          <span className="text-white/70 text-sm font-medium">
            {showPreview ? 'Document Preview' : 'Recording Agreement'}
          </span>
          <span className="text-white/40 text-xs">
            {filledCount}/{FORM_FIELDS.length} fields
          </span>
        </div>

        {/* Progress bar */}
        <div className="px-5 py-2 border-b border-white/5">
          <div className="h-1 bg-white/5 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-primary rounded-full"
              initial={{ width: 0 }}
              animate={{ width: `${progress}%` }}
              transition={{ duration: 0.5, ease: 'easeOut' }}
            />
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          <AnimatePresence mode="wait">
            {showPreview ? (
              <motion.div
                key="preview"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="px-6 py-5"
              >
                <RecordingAgreementPreview formData={formData} />
              </motion.div>
            ) : (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="px-5 py-4 space-y-3"
              >
                {FORM_FIELDS.map((field) => (
                  <FormField
                    key={field.key}
                    field={field}
                    value={formData[field.key]}
                    justFilled={recentlyFilled.has(field.key)}
                  />
                ))}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Create Deal button — only after script finishes */}
        {scriptDone && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.5 }}
            className="px-5 py-4 border-t border-white/5"
          >
            <Button onClick={handleCreateDeal} className="w-full gap-2">
              <Sparkles className="size-4" />
              Create Deal
              <ArrowRight className="size-4" />
            </Button>
          </motion.div>
        )}
      </div>
    </div>
  );
};
